import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, X as XIcon, RefreshCw } from 'lucide-react';
import DailyPnlTable from './DailyPnlTable';
import EmptyState from './EmptyState';
import KpiStripCard from './KpiStripCard';
import EquityCurveCard from './EquityCurveCard';
import DrawdownCard from './DrawdownCard';
import MonthlyHeatmap from './MonthlyHeatmap';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import analyticsService from '../../services/analyticsService';
import type { AnalyticsFilters, AnalyticsSummary, DailyPnlPoint } from '../../types/analytics';
import type { BreakdownSelection } from './BreakdownLeaderboard';

interface Props {
  filters: AnalyticsFilters;
  selection: BreakdownSelection;
  onClear: () => void;
}

function formatPeriod(start: string, end: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Drill-down performance view for a selected strategy / basket from the
 * Breakdown leaderboard. Fetches `/analytics/summary` + `/analytics/daily-pnl`
 * scoped to the selection (the endpoints apply basket/strategy filters
 * server-side via project_pnl_per_day) and renders the same three-card layout
 * the Overview section uses: KPI strip (full width) + Daily P&L (left) +
 * Equity curve / Drawdown stacked (right).
 */
const DrilldownPerformance: React.FC<Props> = ({ filters, selection, onClear }) => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [dailyPnl, setDailyPnl] = useState<DailyPnlPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopedFilters: AnalyticsFilters = {
    ...filters,
    strategies: selection.dimension === 'strategy' ? [selection.key] : [],
    baskets: selection.dimension === 'basket' ? [selection.key] : [],
  };

  const fetchScoped = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryR, dailyR] = await Promise.all([
        analyticsService.getSummary(scopedFilters),
        analyticsService.getDailyPnl(scopedFilters),
      ]);
      setSummary(summaryR);
      setDailyPnl(dailyR.points || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load drill-down.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.start, filters.end, filters.accounts, selection.dimension, selection.key]);

  useEffect(() => { fetchScoped(); }, [fetchScoped]);

  const dimensionLabel = selection.dimension === 'strategy' ? 'Strategy' : 'Basket';
  const hasData = summary && summary.totalTrades > 0;

  return (
    <div className="relative space-y-4">
      {/* Floating clear button — top-right of the whole drill-down block,
          high enough to overlay the KPI strip card's header. */}
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear drill-down"
        className="absolute right-3 top-3 z-10 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
      >
        <XIcon className="w-4 h-4" />
      </button>

      {loading && !summary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading {selection.name}…
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && summary && !hasData && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {selection.name} — Performance
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{dimensionLabel}</span>
          </div>
          <div className="p-6">
            <EmptyState message="No closed trades for this selection in the current filter range." />
          </div>
        </div>
      )}

      {summary && hasData && (
        <>
          {/* Row 1 — KPIs span the full width. */}
          <KpiStripCard
            summary={summary}
            title={`${selection.name} — Performance`}
            period={formatPeriod(filters.start, filters.end)}
          />

          {/* Row 2 — Daily P&L (narrow left) | Equity + Drawdown stacked (right).
              `items-stretch` equalises both column heights; right cards
              flex-grow to share that height with no gap-padding-shift. */}
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
            <div className="lg:col-span-2 min-w-0">
              <Card>
                <CardHeader>
                  <CardTitle>Daily P&amp;L</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <DailyPnlTable
                    points={dailyPnl}
                    rangeStart={filters.start}
                    rangeEnd={filters.end}
                  />
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-5 min-w-0 flex flex-col gap-4">
              <div className="flex-1 min-h-[260px]">
                <EquityCurveCard points={summary.dailyEquity} fill />
              </div>
              <div className="flex-1 min-h-[200px]">
                <DrawdownCard points={summary.dailyDrawdown} fill />
              </div>
            </div>
          </div>

          {/* Row 3 — Year × Month heatmap scoped to this basket / strategy. */}
          <MonthlyHeatmap
            months={summary.monthlyReturns}
            title={`${selection.name} — Monthly Returns`}
          />
        </>
      )}
    </div>
  );
};

export default DrilldownPerformance;
